// PAQUETES
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { eventoBusquedaRealizada } from "../../funcionesDatos/eventoPropio";
import { pixelBusquedaRealizada } from "../../funcionesDatos/pixelFacebook";
import { gtagBusquedaRealizada } from "../../funcionesDatos/pixelGoogle";
import { ttqBusquedaRealizada } from "../../funcionesDatos/pixelTiktok";

// Estado y lógica de búsqueda de servicios, compartido entre /busqueda y /categoria/:categoria.
// Si se pasa categoriaFija, esa categoría queda como valor por defecto y se restaura
// siempre que se reinicien/limpien los filtros (la categoría no se pierde en esa página).
// vistaInicial: "lista" (default, /busqueda y /categoria) o "mapa" (widget embebido de Home,
// que solo necesita la vista mapa, sin disparar la búsqueda paginada de lista).
function useBusquedaServicios({ categoriaFija = null, idUsuario = null, vistaInicial = "lista" } = {}) {
  const [palabra, setPalabra]           = useState(null);
  const [ciudad, setCiudad]             = useState(null);
  const [categoria, setCategoria]       = useState(categoriaFija);
  const [subcategoria, setSubcategoria] = useState(null);
  const [ordenar, setOrdenar]           = useState(null);
  const [precioMax, setPrecioMax]       = useState(null);

  const [cargando, setCargando]                 = useState(true);
  const [categoriasConfig, setCategoriasConfig] = useState([]);
  const [ciudadesDisponibles, setCiudadesDisponibles] = useState([]);
  const [resultados, setResultados]             = useState([]);
  const [error, setError]                       = useState(null);
  const [page, setPage]                         = useState(1);
  const [total, setTotal]                       = useState(0);
  const [totalPaginas, setTotalPaginas]         = useState(1);
  const limit                                   = 8; // 2 filas x 4 columnas (col-lg-3)

  // Vista mapa "cerca de mí" (toggle Lista/Mapa dentro de /busqueda). Estado separado del de la
  // lista porque el mapa no pagina: trae todos los pines EnLocal de la ciudad de una vez.
  const [vista, setVista]                 = useState(vistaInicial); // "lista" | "mapa"
  const [serviciosMapa, setServiciosMapa] = useState([]);
  // "Flotantes": Digital y Domicilio no tienen coordenada real, se muestran alrededor del mapa
  // (no como pines) para que no se vea vacío en ciudades con pocos EnLocal. Ver mapaResultadosBusqueda.jsx.
  const [serviciosDigitales, setServiciosDigitales] = useState([]);
  const [serviciosDomicilio, setServiciosDomicilio] = useState([]);
  const [cargandoMapa, setCargandoMapa]   = useState(false);
  const [mapaHabilitado, setMapaHabilitado] = useState(false);
  const [centroMapa, setCentroMapa]       = useState(null);
  const [mensajeMapa, setMensajeMapa]     = useState(null);
  const [totalCiudadMapa, setTotalCiudadMapa] = useState(0);
  // {lat, lng} real solo cuando la ciudad se resolvió por geolocalización (botón "cerca de mí") —
  // null cuando se eligió a mano en el dropdown, ahí no tenemos ni pedimos su ubicación real. Se usa
  // para que las burbujas floten alrededor de dónde estás de verdad (no del centroide de la ciudad)
  // y para que el título diga "cerca de ti" solo cuando de verdad lo es (ver panelBusquedaServicios.jsx).
  const [miUbicacion, setMiUbicacion]     = useState(null);
  // Evita un fetch duplicado justo después de resolver "cerca de mí" por ubicación (ver
  // cargarServiciosMapaPorUbicacion): esa función ya trae los datos, pero también cambia `ciudad`
  // y `vista`, lo que dispararía de nuevo el efecto de abajo si no se salta una vez.
  const saltarProximaCargaMapaRef = useRef(false);

  // CARGAR CATEGORÍAS CONFIG Y CIUDADES (para los dropdowns de Categoría/Ciudad del buscador)
  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        setError(null);

        const [resCategorias, resPais] = await Promise.all([
          axios.post("/obtener-categorias", { params: {} }),
          axios.post("/obtener-info-pais-total", { params: { pais: "Colombia" } }),
        ]);

        const arr = resCategorias?.data?.categoriasConfig || resCategorias?.data?.categorias || [];
        setCategoriasConfig(Array.isArray(arr) ? arr : []);

        const ciudadesPais = resPais?.data?.contenidoPais?.ciudades || [];
        setCiudadesDisponibles(Array.isArray(ciudadesPais) ? ciudadesPais : []);
      }
      catch (e) {
        setCategoriasConfig([]);
        setCiudadesDisponibles([]);
      }
      finally { setCargando(false); }
    })();
  }, []);

  const subcategoriasDisponibles = useMemo(() => {
    if (!categoria) return [];

    const obj  = (categoriasConfig || []).find((c) => c.categoria === categoria);
    const subs = obj?.subcategorias || [];
    return Array.isArray(subs) ? subs : [];
  }, [categoria, categoriasConfig]);

  const imageCate = useMemo(() => {
    const arr = (categoriasConfig || [])
      .map((c) => c?.imagen)
      .filter(Boolean);
    return Array.isArray(arr) ? arr : [];
  }, [categoriasConfig]);

  // BUSCAR (POST) CON FILTROS
  // subcategoriaOverride: permite buscar con una subcategoría distinta a la del estado actual
  // en el mismo click (ej. chips rápidos), sin esperar al siguiente render para leerla del state.
  async function ejecutarBusqueda({ reiniciar = false, pageOverride, subcategoriaOverride } = {}) {
    try {
      setError(null);
      setCargando(true);

      const paginaEnviar = Number.isFinite(Number(pageOverride)) && Number(pageOverride) > 0 ? Number(pageOverride) : page;
      const subcategoriaEnviar = typeof subcategoriaOverride !== "undefined" ? subcategoriaOverride : subcategoria;

      const payload = {
        palabra: reiniciar ? null : palabra,
        ciudad: reiniciar ? null : ciudad,
        categoria: reiniciar ? (categoriaFija ?? null) : categoria,
        subcategoria: reiniciar ? null : subcategoriaEnviar,
        ordenar: reiniciar ? null : ordenar,
        precioMax: reiniciar ? null : precioMax,
        page: paginaEnviar,
        limit,
        idUsuario: idUsuario || null,
        origen: "web"
      };

      const res = await axios.post("/buscar-servicios", { params: payload });
      const lista = res?.data?.servicios || [];
      setResultados(Array.isArray(lista) ? lista : []);

      const paginaActualRes = Number(res?.data?.paginaActual || 1);
      const totalRes = Number(res?.data?.total || 0);
      const totalPaginasRes = Number(res?.data?.totalPaginas || 1);

      setTotal(Number.isFinite(totalRes) ? totalRes : 0);
      setTotalPaginas(Number.isFinite(totalPaginasRes) && totalPaginasRes > 0 ? totalPaginasRes : 1);

      eventoBusquedaRealizada({
        termino: payload.palabra,
        categoria: payload.categoria,
        subcategoria: payload.subcategoria,
        ciudad: payload.ciudad,
        ordenar: payload.ordenar,
        precioMax: payload.precioMax,
        idBusqueda: res?.data?.idBusqueda || null,
        cantidadResultados: totalRes,
        usuarioId: idUsuario,
      });
      pixelBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria });
      gtagBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria, subcategoria: payload.subcategoria, ciudad: payload.ciudad, ordenar: payload.ordenar });
      ttqBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria });

      if (Number.isFinite(paginaActualRes) && paginaActualRes > 0 && paginaActualRes !== paginaEnviar) {
        setPage(paginaActualRes);
      }

      if (reiniciar) {
        setPalabra(null);
        setCiudad(null);
        setCategoria(categoriaFija ?? null);
        setSubcategoria(null);
        setOrdenar(null);
        setPrecioMax(null);
        setPage(1);
        setTotal(0);
        setTotalPaginas(1);
      }
    }
    catch (e) {
      setResultados([]);
      setTotal(0);
      setTotalPaginas(1);
      setError(e?.response?.data?.mensaje || "No se pudo realizar la búsqueda.");
    } finally {
      setCargando(false);
    }
  }

  // BUSCAR SERVICIOS PARA EL MAPA (sin paginación, refleja los mismos filtros activos)
  // subcategoriaOverride: mismo motivo que en ejecutarBusqueda — cuando se llama justo después de un
  // setSubcategoria (chip rápido), el closure todavía tiene el valor viejo hasta el próximo render.
  async function cargarServiciosMapa({ subcategoriaOverride } = {}) {
    if (!ciudad) {
      setServiciosMapa([]);
      setServiciosDigitales([]);
      setServiciosDomicilio([]);
      setMapaHabilitado(false);
      setCentroMapa(null);
      setTotalCiudadMapa(0);
      setMensajeMapa("Escribe una ciudad para ver el mapa.");
      setMiUbicacion(null);
      return;
    }

    try {
      setCargandoMapa(true);
      // Ciudad elegida a mano (dropdown) — no es necesariamente donde estás de verdad, así que se
      // limpia cualquier ubicación real que hubiera quedado de un "cerca de mí" anterior.
      setMiUbicacion(null);

      const subcategoriaEnviar = typeof subcategoriaOverride !== "undefined" ? subcategoriaOverride : subcategoria;
      const payload = { ciudad, categoria, subcategoria: subcategoriaEnviar, palabra, precioMax, idUsuario: idUsuario || null };
      const res = await axios.post("/mapa-servicios-cerca", { params: payload });

      const serviciosMapaRes = Array.isArray(res?.data?.servicios) ? res.data.servicios : [];
      setServiciosMapa(serviciosMapaRes);
      setServiciosDigitales(Array.isArray(res?.data?.serviciosDigitales) ? res.data.serviciosDigitales : []);
      setServiciosDomicilio(Array.isArray(res?.data?.serviciosDomicilio) ? res.data.serviciosDomicilio : []);
      setMapaHabilitado(Boolean(res?.data?.habilitada));
      setCentroMapa(res?.data?.centro || null);
      setTotalCiudadMapa(Number(res?.data?.totalCiudad || 0));
      setMensajeMapa(res?.data?.mensaje || null);

      eventoBusquedaRealizada({
        termino: payload.palabra,
        categoria: payload.categoria,
        subcategoria: payload.subcategoria,
        ciudad: payload.ciudad,
        precioMax: payload.precioMax,
        idBusqueda: res?.data?.idBusqueda || null,
        cantidadResultados: serviciosMapaRes.length,
        usuarioId: idUsuario,
      });
      pixelBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria });
      gtagBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria, subcategoria: payload.subcategoria, ciudad: payload.ciudad });
      ttqBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria });
    }
    catch (e) {
      setServiciosMapa([]);
      setServiciosDigitales([]);
      setServiciosDomicilio([]);
      setMapaHabilitado(false);
      setCentroMapa(null);
      setMensajeMapa(e?.response?.data?.mensaje || "No se pudo cargar el mapa.");
    } finally {
      setCargandoMapa(false);
    }
  }

  // BUSCAR SERVICIOS PARA EL MAPA A PARTIR DE LA UBICACIÓN DEL NAVEGADOR (botón "cerca de mí" en
  // Búsqueda) — mismos filtros activos (categoría/subcategoría/precio) que ya tiene el buscador,
  // pero resuelve la ciudad por lat/lng en vez del dropdown. Si queda habilitado, sincroniza
  // `ciudad` con la que devolvió el backend y cambia a vista "mapa" (así el dropdown y el resto
  // del buscador quedan consistentes, y el mapa reemplaza a la lista de resultados). Devuelve
  // true/false según si quedó habilitado, para que el botón sepa si debe ocultarse.
  async function cargarServiciosMapaPorUbicacion(lat, lng) {
    try {
      setCargandoMapa(true);

      const payload = { lat, lng, categoria, subcategoria, palabra, precioMax, idUsuario: idUsuario || null };
      const res = await axios.post("/mapa-servicios-cerca", { params: payload });
      const habilitada = Boolean(res?.data?.habilitada);

      const serviciosMapaRes = Array.isArray(res?.data?.servicios) ? res.data.servicios : [];
      setServiciosMapa(serviciosMapaRes);
      setServiciosDigitales(Array.isArray(res?.data?.serviciosDigitales) ? res.data.serviciosDigitales : []);
      setServiciosDomicilio(Array.isArray(res?.data?.serviciosDomicilio) ? res.data.serviciosDomicilio : []);
      setMapaHabilitado(habilitada);
      setCentroMapa(res?.data?.centro || null);
      setTotalCiudadMapa(Number(res?.data?.totalCiudad || 0));
      setMensajeMapa(res?.data?.mensaje || null);

      eventoBusquedaRealizada({
        termino: payload.palabra,
        categoria: payload.categoria,
        subcategoria: payload.subcategoria,
        ciudad: res?.data?.ciudad || null,
        precioMax: payload.precioMax,
        idBusqueda: res?.data?.idBusqueda || null,
        cantidadResultados: serviciosMapaRes.length,
        usuarioId: idUsuario,
      });
      pixelBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria });
      gtagBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria, subcategoria: payload.subcategoria, ciudad: res?.data?.ciudad || null });
      ttqBusquedaRealizada({ termino: payload.palabra, categoria: payload.categoria });

      if (habilitada) {
        saltarProximaCargaMapaRef.current = true;
        setCiudad(res?.data?.ciudad || null);
        setMiUbicacion({ lat, lng });
        setVista("mapa");
      }
      else {
        setMiUbicacion(null);
      }

      return habilitada;
    }
    catch (e) {
      setMapaHabilitado(false);
      setMensajeMapa(e?.response?.data?.mensaje || "No se pudo cargar el mapa.");
      return false;
    } finally {
      setCargandoMapa(false);
    }
  }

  // Carga los servicios del mapa la primera vez que se activa la vista mapa (botón "cerca de mí" o
  // navegación con vista=mapa en el state). A propósito NO se recarga solo con cambiar ciudad,
  // categoría, etc. mientras ya está en el mapa — eso reubicaría/recargaría el mapa en cada cambio de
  // dropdown; el usuario debe oprimir "Buscar" (ver panelBusquedaServicios.jsx, que llama
  // cargarServiciosMapa directamente ahí) para aplicar los nuevos filtros al mapa.
  useEffect(() => {
    if (vista !== "mapa") return;

    if (saltarProximaCargaMapaRef.current) {
      saltarProximaCargaMapaRef.current = false;
      return;
    }

    if (!ciudad) {
      setVista("lista");
      return;
    }

    cargarServiciosMapa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista]);

  // Si el usuario borra la ciudad (selecciona "Todas" en el dropdown) mientras el mapa está activo,
  // no tiene sentido seguir en vista mapa (no hay ciudad para centrarlo) — se vuelve a la lista sola.
  useEffect(() => {
    if (vista === "mapa" && !ciudad) {
      setVista("lista");
    }
  }, [ciudad, vista]);

  function limpiarFiltros() {
    setPalabra(null);
    setCiudad(null);
    setCategoria(categoriaFija ?? null);
    setSubcategoria(null);
    setOrdenar(null);
    setPrecioMax(null);
    setPage(1);
    setError(null);

    ejecutarBusqueda({ reiniciar: true, pageOverride: 1 });
  }

  return {
    palabra, setPalabra,
    ciudad, setCiudad,
    categoria, setCategoria,
    subcategoria, setSubcategoria,
    ordenar, setOrdenar,
    precioMax, setPrecioMax,
    cargando,
    categoriasConfig,
    ciudadesDisponibles,
    resultados,
    error,
    page, setPage,
    total,
    totalPaginas,
    subcategoriasDisponibles,
    imageCate,
    ejecutarBusqueda,
    limpiarFiltros,
    // vista mapa "cerca de mí"
    vista, setVista,
    serviciosMapa,
    serviciosDigitales,
    serviciosDomicilio,
    cargandoMapa,
    mapaHabilitado,
    centroMapa,
    mensajeMapa,
    totalCiudadMapa,
    miUbicacion,
    cargarServiciosMapa,
    cargarServiciosMapaPorUbicacion,
  };
}

export default useBusquedaServicios;
